import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ChevronRight, X, Sparkles, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchProgress = useCallback(async () => {
    try {
      const r = await fetch(`${API}/checklist/progress`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } catch (e) {
      console.error("Checklist fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const markComplete = async (stepId) => {
    try {
      await fetch(`${API}/checklist/progress/${stepId}/complete`, {
        method: "POST",
        credentials: "include",
      });
      fetchProgress();
    } catch (e) { console.error("Failed to complete step:", e); }
  };

  const dismiss = async () => {
    setDismissing(true);
    try {
      await fetch(`${API}/checklist/dismiss`, {
        method: "POST",
        credentials: "include",
      });
      setData((prev) => (prev ? { ...prev, show_checklist: false } : prev));
    } catch (e) { console.error("Failed to dismiss checklist:", e); } finally {
      setDismissing(false);
    }
  };

  if (loading || !data || !data.show_checklist) return null;

  const pct = data.total > 0 ? Math.round((data.completed_count / data.total) * 100) : 0;
  const nextStep = data.steps.find((s) => !s.completed);

  return (
    <div
      data-testid="onboarding-banner"
      className="bg-gradient-to-r from-[#0d2818] to-[#1a1a1a] border border-emerald-500/20 rounded-xl overflow-hidden"
    >
      {/* Compact header — always visible */}
      <div className="p-4 sm:p-5 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-white truncate">Getting Started</h3>
            <span className="text-xs text-emerald-400 font-medium">{data.completed_count}/{data.total}</span>
          </div>
          <Progress value={pct} className="h-1.5 bg-white/10 [&>div]:bg-emerald-500" />
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            data-testid="onboarding-expand-btn"
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-gray-400 hover:text-white hover:bg-white/10 h-8 px-2"
          >
            <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </Button>
          <Button
            data-testid="onboarding-dismiss-btn"
            variant="ghost"
            size="sm"
            onClick={dismiss}
            disabled={dismissing}
            className="text-gray-500 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Expanded steps list */}
      {expanded && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-2 border-t border-white/5 pt-3">
          {data.steps.map((step) => (
            <div
              key={step.step_id}
              data-testid={`checklist-step-${step.step_id}`}
              className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                step.completed
                  ? "bg-emerald-500/5 opacity-60"
                  : "bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer"
              }`}
              onClick={() => {
                if (!step.completed && !step.auto_detect) markComplete(step.step_id);
              }}
            >
              {step.completed ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-gray-600 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${step.completed ? "text-gray-500 line-through" : "text-white"}`}>
                  {step.title}
                </p>
                {!step.completed && step.description && (
                  <p className="text-xs text-gray-500 truncate">{step.description}</p>
                )}
              </div>
              {!step.completed && step.auto_detect && (
                <span className="text-[10px] uppercase tracking-wider text-gray-600 shrink-0">Auto</span>
              )}
            </div>
          ))}

          <div className="flex items-center gap-2 pt-2">
            <Button
              data-testid="onboarding-view-all-btn"
              variant="outline"
              size="sm"
              onClick={() => navigate("/setup")}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 text-xs"
            >
              View Full Checklist
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Quick next step hint when collapsed */}
      {!expanded && nextStep && (
        <div className="px-4 sm:px-5 pb-3 -mt-1">
          <p className="text-xs text-gray-500">
            Next: <span className="text-gray-400">{nextStep.title}</span>
          </p>
        </div>
      )}
    </div>
  );
}
